import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Smartphone, 
  Download, 
  Share, 
  MoreVertical,
  Plus,
  ArrowLeft,
  Apple,
  Chrome
} from 'lucide-react';

export default function InstallPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [deviceType, setDeviceType] = useState('desktop');
  const [appUrl, setAppUrl] = useState('');

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType('ios');
    } else if (/android/.test(userAgent)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }

    // Get current URL for QR code
    setAppUrl(window.location.origin);
  }, []);

  const texts = {
    it: {
      title: 'Installa l\'App',
      subtitle: 'SMART DATA COLLECTION',
      scanQR: 'Scansiona il QR Code',
      scanDesc: 'Usa la fotocamera del tuo smartphone per scansionare il codice QR',
      iosTitle: 'Istruzioni per iPhone/iPad',
      iosStep1: 'Apri questa pagina in Safari',
      iosStep2: 'Tocca il pulsante Condividi',
      iosStep3: 'Scorri e seleziona "Aggiungi a Home"',
      iosStep4: 'Conferma toccando "Aggiungi"',
      androidTitle: 'Istruzioni per Android',
      androidStep1: 'Apri questa pagina in Chrome',
      androidStep2: 'Tocca il menu (⋮) in alto a destra',
      androidStep3: 'Seleziona "Installa app" o "Aggiungi a Home"',
      androidStep4: 'Conferma l\'installazione',
      yourDevice: 'Il tuo dispositivo',
      back: 'Indietro',
      printQR: 'Stampa QR Code',
      benefits: 'Vantaggi dell\'installazione',
      benefit1: 'Accesso rapido dalla Home',
      benefit2: 'Funziona offline',
      benefit3: 'Esperienza fullscreen',
      benefit4: 'Nessun download da store'
    },
    en: {
      title: 'Install the App',
      subtitle: 'SMART DATA COLLECTION',
      scanQR: 'Scan the QR Code',
      scanDesc: 'Use your smartphone camera to scan the QR code',
      iosTitle: 'Instructions for iPhone/iPad',
      iosStep1: 'Open this page in Safari',
      iosStep2: 'Tap the Share button',
      iosStep3: 'Scroll and select "Add to Home Screen"',
      iosStep4: 'Confirm by tapping "Add"',
      androidTitle: 'Instructions for Android',
      androidStep1: 'Open this page in Chrome',
      androidStep2: 'Tap the menu (⋮) at top right',
      androidStep3: 'Select "Install app" or "Add to Home Screen"',
      androidStep4: 'Confirm the installation',
      yourDevice: 'Your device',
      back: 'Back',
      printQR: 'Print QR Code',
      benefits: 'Installation Benefits',
      benefit1: 'Quick access from Home',
      benefit2: 'Works offline',
      benefit3: 'Fullscreen experience',
      benefit4: 'No store download needed'
    }
  };

  const txt = texts[language] || texts.it;

  return (
    <CyberLayout>
      <div className="min-h-screen flex flex-col items-center justify-start px-4 py-10 sm:py-16">
        <div className="max-w-4xl w-full">
          
          {/* Header */}
          <div className="text-center mb-8 fade-in-up">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyber-gray border border-neon-green/30 mb-4">
              <Download className="w-8 h-8 text-neon-green" />
            </div>
            <h1 className="font-orbitron text-2xl sm:text-3xl font-bold text-white mb-2 tracking-wider">
              {txt.title}
            </h1>
            <p className="font-mono text-neon-cyan text-sm tracking-widest">
              {txt.subtitle}
            </p>
          </div>

          {/* QR Code Section - Desktop */}
          {deviceType === 'desktop' && (
            <div className="cyber-card rounded-lg p-8 mb-8 fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* QR Code */}
                <div className="flex-shrink-0">
                  <div className="p-4 bg-white rounded-lg shadow-lg shadow-neon-green/20">
                    <QRCodeSVG 
                      value={appUrl}
                      size={180}
                      level="H"
                      includeMargin={true}
                      fgColor="#1a1a2e"
                      bgColor="#ffffff"
                    />
                  </div>
                </div>
                
                {/* Instructions */}
                <div className="flex-1 text-center md:text-left">
                  <h2 className="font-orbitron text-xl text-white mb-3 flex items-center justify-center md:justify-start gap-2">
                    <Smartphone className="w-5 h-5 text-neon-green" />
                    {txt.scanQR}
                  </h2>
                  <p className="text-gray-400 font-mono text-sm mb-6">
                    {txt.scanDesc}
                  </p>
                  
                  {/* Benefits */}
                  <div className="grid grid-cols-2 gap-3">
                    {[txt.benefit1, txt.benefit2, txt.benefit3, txt.benefit4].map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs font-mono text-neon-cyan/80">
                        <div className="w-1.5 h-1.5 rounded-full bg-neon-green" />
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Instructions Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            
            {/* iOS Instructions */}
            <div 
              className={`cyber-card rounded-lg p-6 fade-in-up transition-all duration-300 ${
                deviceType === 'ios' ? 'ring-2 ring-neon-green shadow-lg shadow-neon-green/20' : ''
              }`}
              style={{ animationDelay: '0.2s' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                  <Apple className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-orbitron text-white text-sm">{txt.iosTitle}</h3>
                  {deviceType === 'ios' && (
                    <span className="text-xs font-mono text-neon-green">★ {txt.yourDevice}</span>
                  )}
                </div>
              </div>
              
              <ol className="space-y-3">
                {[txt.iosStep1, txt.iosStep2, txt.iosStep3, txt.iosStep4].map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-neon-cyan/20 flex items-center justify-center text-xs font-mono text-neon-cyan">
                      {idx + 1}
                    </span>
                    <span className="text-sm text-gray-300 font-mono pt-0.5">
                      {step}
                      {idx === 1 && <Share className="w-4 h-4 inline ml-1 text-neon-cyan" />}
                      {idx === 2 && <Plus className="w-4 h-4 inline ml-1 text-neon-cyan" />}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Android Instructions */}
            <div 
              className={`cyber-card rounded-lg p-6 fade-in-up transition-all duration-300 ${
                deviceType === 'android' ? 'ring-2 ring-neon-green shadow-lg shadow-neon-green/20' : ''
              }`}
              style={{ animationDelay: '0.3s' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-900/50 flex items-center justify-center">
                  <Chrome className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="font-orbitron text-white text-sm">{txt.androidTitle}</h3>
                  {deviceType === 'android' && (
                    <span className="text-xs font-mono text-neon-green">★ {txt.yourDevice}</span>
                  )}
                </div>
              </div>
              
              <ol className="space-y-3">
                {[txt.androidStep1, txt.androidStep2, txt.androidStep3, txt.androidStep4].map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-xs font-mono text-green-400">
                      {idx + 1}
                    </span>
                    <span className="text-sm text-gray-300 font-mono pt-0.5">
                      {step}
                      {idx === 1 && <MoreVertical className="w-4 h-4 inline ml-1 text-green-400" />}
                      {idx === 2 && <Download className="w-4 h-4 inline ml-1 text-green-400" />}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Mobile QR Code (for mobile users to show others) */}
          {deviceType !== 'desktop' && (
            <div className="cyber-card rounded-lg p-6 mb-8 fade-in-up text-center" style={{ animationDelay: '0.4s' }}>
              <h3 className="font-orbitron text-white text-sm mb-4">{txt.scanQR}</h3>
              <div className="inline-block p-3 bg-white rounded-lg">
                <QRCodeSVG 
                  value={appUrl}
                  size={120}
                  level="H"
                  includeMargin={true}
                  fgColor="#1a1a2e"
                  bgColor="#ffffff"
                />
              </div>
              <p className="text-xs text-gray-500 font-mono mt-3">{txt.scanDesc}</p>
            </div>
          )}

          {/* Back Button */}
          <div className="flex justify-center fade-in-up" style={{ animationDelay: '0.5s' }}>
            <button
              onClick={() => navigate('/')}
              className="cyber-btn-outline px-6 py-3 flex items-center gap-2 rounded-md"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{txt.back}</span>
            </button>
          </div>

        </div>
      </div>
    </CyberLayout>
  );
}
